import { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createSsoProvider,
  deleteSsoProvider,
  listSsoProviders,
  updateSsoProvider,
  type SsoKind,
  type SsoProvider,
} from "@/api/sso";
import { PageHeader } from "@/components/PageHeader";

const KIND_HELP: Record<SsoKind, string> = {
  GOOGLE: "L'issuer est pré-rempli (accounts.google.com).",
  MICROSOFT:
    "Issuer obligatoire : https://login.microsoftonline.com/<tenant-id>/v2.0. Ne pas utiliser « common », qui accepterait n'importe quel annuaire.",
  OIDC: "Issuer obligatoire — l'URL de base, sans /.well-known/openid-configuration.",
};

const ERRORS: Record<string, string> = {
  issuer_required_for_kind: "Cet IdP exige un issuer explicite.",
  idp_unreachable:
    "Impossible de joindre l'issuer. Vérifiez l'URL et l'accès réseau depuis le serveur.",
  invalid_discovery_document: "L'issuer répond, mais son document de découverte est incomplet.",
  tenant_wide_permission_required:
    "Réservé aux administrateurs du tenant : un IdP ouvre l'accès à tous les comptes.",
};

function errorText(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
  if (typeof detail === "string") return ERRORS[detail] ?? detail;
  if (detail && typeof detail === "object") {
    const key = (detail as { error?: string }).error ?? "";
    return ERRORS[key] ?? key ?? "Échec";
  }
  return "Échec";
}

export function SsoAdminPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [kind, setKind] = useState<SsoKind>("GOOGLE");
  const [displayName, setDisplayName] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [issuer, setIssuer] = useState("");
  const [domains, setDomains] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["sso-providers"],
    queryFn: listSsoProviders,
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["sso-providers"] });

  const create = useMutation({
    mutationFn: () =>
      createSsoProvider({
        kind,
        display_name: displayName.trim(),
        client_id: clientId.trim(),
        client_secret: clientSecret.trim() || undefined,
        issuer: issuer.trim() || undefined,
        allowed_email_domains: domains
          .split(",")
          .map((d) => d.trim())
          .filter(Boolean),
      }),
    onSuccess: () => {
      setOpen(false);
      setDisplayName("");
      setClientId("");
      setClientSecret("");
      setIssuer("");
      setDomains("");
      setError(null);
      invalidate();
    },
    onError: (e) => setError(errorText(e)),
  });

  const toggle = useMutation({
    mutationFn: (p: SsoProvider) => updateSsoProvider(p.id, { enabled: !p.enabled }),
    onSuccess: invalidate,
    onError: (e) => setError(errorText(e)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteSsoProvider(id),
    onSuccess: invalidate,
    onError: (e) => setError(errorText(e)),
  });

  const providers = data ?? [];

  return (
    <Stack spacing={2}>
      <PageHeader
        title="Authentification SSO"
        subtitle="Fournisseurs d'identité de votre tenant. Un compte Cortex doit exister au préalable : le SSO ne crée jamais de compte."
        actions={
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => setOpen(true)}>
            Ajouter
          </Button>
        }
      />

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <CircularProgress size={24} />
      ) : providers.length === 0 ? (
        <Alert severity="info">
          Aucun fournisseur configuré. Les utilisateurs se connectent uniquement par mot de passe.
        </Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nom</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Issuer</TableCell>
              <TableCell>Domaines autorisés</TableCell>
              <TableCell align="center">Comptes liés</TableCell>
              <TableCell align="center">Actif</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {providers.map((p) => (
              <TableRow key={p.id}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{p.display_name}</Typography>
                    {!p.has_client_secret && (
                      <Tooltip title="Aucun secret client enregistré — le flux échouera si l'IdP en exige un.">
                        <Chip size="small" color="warning" label="sans secret" />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>{p.kind}</TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                    {p.issuer}
                  </Typography>
                </TableCell>
                <TableCell>
                  {p.allowed_email_domains.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      tous
                    </Typography>
                  ) : (
                    p.allowed_email_domains.join(", ")
                  )}
                </TableCell>
                <TableCell align="center">{p.linked_identities}</TableCell>
                <TableCell align="center">
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={p.enabled}
                        onChange={() => toggle.mutate(p)}
                      />
                    }
                    label=""
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Copier l'URI de redirection à déclarer chez l'IdP">
                    <IconButton
                      size="small"
                      onClick={() => void navigator.clipboard.writeText(p.redirect_uri)}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      p.linked_identities > 0
                        ? `Supprime aussi ${p.linked_identities} association(s) de compte`
                        : "Supprimer"
                    }
                  >
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => remove.mutate(p.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Ajouter un fournisseur</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="sso-kind">Type</InputLabel>
              <Select
                labelId="sso-kind"
                label="Type"
                value={kind}
                onChange={(e) => setKind(e.target.value as SsoKind)}
              >
                <MenuItem value="GOOGLE">Google Workspace</MenuItem>
                <MenuItem value="MICROSOFT">Microsoft Entra ID</MenuItem>
                <MenuItem value="OIDC">OIDC générique</MenuItem>
              </Select>
            </FormControl>
            <Alert severity="info">{KIND_HELP[kind]}</Alert>
            <TextField
              label="Nom affiché"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              helperText="Apparaît sur le bouton de connexion."
              fullWidth
            />
            {kind !== "GOOGLE" && (
              <TextField
                label="Issuer"
                value={issuer}
                onChange={(e) => setIssuer(e.target.value)}
                placeholder="https://login.microsoftonline.com/<tenant-id>/v2.0"
                fullWidth
              />
            )}
            <TextField
              label="Client ID"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              fullWidth
            />
            <TextField
              label="Client secret"
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              helperText="Chiffré en base. Jamais renvoyé par l'API."
              fullWidth
            />
            <TextField
              label="Domaines autorisés"
              value={domains}
              onChange={(e) => setDomains(e.target.value)}
              placeholder="chu-paris.fr, samu75.fr"
              helperText="Séparés par des virgules. Vide = aucune restriction de domaine."
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            disabled={!displayName.trim() || !clientId.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Vérification de l'issuer…" : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
